import React, { useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Info,
  Lightbulb,
  BookOpen,
  Target,
  Grid,
  List,
  Hash
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
 // Helper to get icon for each section/category
export const getIcon = (title, category) => {
  const lowerTitle = title?.toLowerCase() || '';
  const categoryName = category?.name?.toLowerCase() || '';

  if (lowerTitle.includes('pattern') || categoryName.includes('pattern')) {
    return <Grid size={18} />;
  }
  if (lowerTitle.includes('formation') || lowerTitle.includes('conjugation')) {
    return <Hash size={18} />;
  }
  if (lowerTitle.includes('regel') || lowerTitle.includes('rule')) {
    return <BookOpen size={18} />;
  }
  if (lowerTitle.includes('beispiel') || lowerTitle.includes('example')) {
    return <Target size={18} />;
  }
  if (lowerTitle.includes('detail') || lowerTitle.includes('overview')) {
    return <List size={18} />;
  }
  return <Info size={18} />;
};

const InteractiveGrammarContent = ({
  section,
  isMobile,
  userProgress,
  pillar,
  level
}) => {
    const { t } = useTranslation();

  const processText = (text) => {
    if (typeof text !== 'string') return text;
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, index) => {
      return index % 2 === 1 ? <strong key={index}>{part}</strong> : part;
    });
  };

  // Smart component that auto-processes text
  const SmartText = ({ children, as: Component = 'span', ...props }) => {
    const processedContent = typeof children === 'string' ? processText(children) : children;
    return <Component {...props}>{processedContent}</Component>;
  };

  // For table cells specifically
  const SmartTableCell = ({ children, ...props }) => (
    <td {...props}>
      <SmartText>{children}</SmartText>
    </td>
  );

  // Keep the old function for backward compatibility
  const formatWithBold = processText;

// REPLACE the entire customStyles object in InteractiveGrammarContent.jsx
// Starting around line 56

const customStyles = {
  container: {
    maxWidth: '100%',
    padding: isMobile ? '0' : '1rem',
  },

  // ===== MAIN CARDS - Pure White with Black Borders =====
  card: {
  background: '#FFFFFF',
  borderRadius: isMobile ? '0.75rem' : '1rem',
  marginBottom: '1rem',
  overflow: 'hidden',
  border: '2px solid #000000',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  position: 'relative'
},

  cardHeader: {
    padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem',
    background: '#F9FAFB',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'all 0.3s ease',
    borderBottom: '2px solid #E5E7EB'
  },

  cardHeaderActive: {
    background: '#000000',
    color: 'white',
    borderBottom: '2px solid #000000'
  },

  cardTitle: {
    fontSize: isMobile ? '1rem' : '1.1rem',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flex: 1
  },

  cardIcon: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.1)',
    flexShrink: 0
  },

  cardContent: {
    padding: isMobile ? '1rem' : '1.5rem',
    background: '#FFFFFF'
  },

  // ===== TABLES - Clean Design =====
  tableContainer: {
    border: '2px solid #000000',
    borderRadius: isMobile ? '0.5rem' : '0.75rem',
    overflow: 'hidden',
    marginTop: '1.5rem',
    background: '#FFFFFF'
  },

  tableHeader: {
    background: '#F9FAFB',
    padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.25rem',
    fontWeight: '600',
    color: '#000000',
    borderBottom: '2px solid #000000'
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: isMobile ? '0.85rem' : '0.95rem',
    background: '#FFFFFF'
  },

  tableCell: {
    padding: isMobile ? '0.6rem' : '0.75rem',
    borderBottom: '1px solid #E5E7EB'
  },

  // ===== BOXES - Light Gray Background =====
  patternBox: {
    background: '#F9FAFB',
    border: '2px solid #E5E7EB',
    borderRadius: isMobile ? '0.375rem' : '0.5rem',
    padding: isMobile ? '0.75rem' : '1rem',
    marginBottom: '1rem',
    position: 'relative'
  },

  ruleCard: {
    background: '#F9FAFB',
    borderLeft: '4px solid #000000',
    borderRadius: isMobile ? '0.375rem' : '0.5rem',
    padding: isMobile ? '0.75rem' : '1rem',
    marginBottom: '1rem',
    border: '2px solid #E5E7EB'
  },

  usageCard: {
    background: '#F9FAFB',
    border: '2px solid #E5E7EB',
    borderRadius: isMobile ? '0.375rem' : '0.5rem',
    padding: isMobile ? '0.75rem' : '1rem',
    marginTop: '1rem',
    fontSize: isMobile ? '0.9rem' : '0.95rem',
    color: '#374151',
    lineHeight: '1.6'
  },

  // ===== EXAMPLE CARDS - Keep Light Green for Recognition =====
  exampleCard: {
  background: '#F7FEF9',
  border: '2px solid #D1FAE5',
  borderRadius: isMobile ? '0.375rem' : '0.5rem',
  padding: isMobile ? '0.5rem' : '1rem',
  marginBottom: isMobile ? '0.5rem' : '0.75rem',
  position: 'relative',
  paddingLeft: isMobile ? '2rem' : '3rem',
  fontSize: isMobile ? '0.85rem' : '0.95rem',
},

  exampleIcon: {
  position: 'absolute',
  left: isMobile ? '0.75rem' : '1rem',
  top: isMobile ? '0.75rem' : '1rem',
  color: '#059669'
},

  // ===== CODE HIGHLIGHTS =====
  codeHighlight: {
    background: '#000000',
    color: '#FFFFFF',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    fontFamily: 'monospace',
    fontSize: isMobile ? '0.8rem' : '0.9rem'
  },

  // ===== DETAIL CARDS =====
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: '1rem',
    marginBottom: '1rem'
  },

  detailCard: {
    background: '#FFFFFF',
    border: '2px solid #E5E7EB',
    borderRadius: isMobile ? '0.375rem' : '0.5rem',
    padding: isMobile ? '0.6rem' : '1rem',
    fontSize: isMobile ? '0.85rem' : '0.95rem',
    position: 'relative'
  },

  // ===== TIP & NOTE CARDS - Subtle Colors =====
  tipCard: {
  background: '#FEFCE8',
  borderLeft: '4px solid #CA8A04',
  borderRadius: isMobile ? '0.375rem' : '0.5rem',
  padding: isMobile ? '0.75rem' : '1rem',
  marginTop: '1rem',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.75rem',
  border: '2px solid #FDE68A'
},

 noteCard: {
  background: '#FEFCE8',
  borderLeft: '4px solid #CA8A04',
  borderRadius: isMobile ? '0.375rem' : '0.5rem',
  padding: isMobile ? '0.75rem' : '1rem',
  marginTop: '1rem',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.75rem',
  color: '#713F12',
  border: '2px solid #FEF3C7'
},

  analysisCard: {
    background: '#F9FAFB',
    borderLeft: '4px solid #000000',
    borderRadius: isMobile ? '0.375rem' : '0.5rem',
    padding: isMobile ? '0.75rem' : '1rem',
    fontSize: isMobile ? '0.85rem' : '0.9rem',
    color: '#374151',
    border: '2px solid #E5E7EB'
  },

  masterExampleCard: {
    background: '#FFFFFF',
    border: '2px solid #000000',
    borderRadius: isMobile ? '0.75rem' : '1rem',
    padding: isMobile ? '1rem' : '1.5rem',
    marginBottom: '1rem',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
    position: 'relative'
  },

  highlightBox: {
  background: '#FEFCE8',
  borderLeft: '4px solid #CA8A04',
  padding: isMobile ? '0.75rem' : '1.5rem',
  margin: isMobile ? '0.5rem 0' : '1rem 0',
  width: '100%',
  boxSizing: 'border-box',
  marginBottom: '1rem',
  fontSize: isMobile ? '0.95rem' : '1rem',
  lineHeight: '1.6',
  border: '2px solid #FEF3C7',
  borderRadius: isMobile ? '0.375rem' : '0.5rem'
},
  subCategoryCard: {
    background: '#F9FAFB',
    border: '2px solid #E5E7EB',
    borderRadius: isMobile ? '0.5rem' : '0.75rem',
    padding: isMobile ? '0.75rem' : '1.25rem',
    marginBottom: isMobile ? '0.75rem' : '1rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    fontSize: isMobile ? '0.85rem' : '0.95rem',
    position: 'relative'
  },

  stepBox: {
    background: '#F9FAFB',
    border: '2px solid #E5E7EB',
    borderRadius: isMobile ? '0.375rem' : '0.5rem',
    padding: isMobile ? '0.75rem' : '1rem',
    marginBottom: '1rem'
  },

  modalCard: {
    background: '#FFFFFF',
    border: '2px solid #E5E7EB',
    borderRadius: isMobile ? '0.75rem' : '1rem',
    padding: isMobile ? '0.75rem' : '1rem',
    marginBottom: '1rem',
    position: 'relative'
  },

  irregularGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: '0.5rem',
    background: '#F9FAFB',
    border: '2px solid #E5E7EB',
    borderRadius: isMobile ? '0.375rem' : '0.5rem',
    padding: isMobile ? '0.75rem' : '1rem'
  },

  conjunctionCategoryCard: {
    background: '#F9FAFB',
    borderLeft: '4px solid #000000',
    borderRadius: isMobile ? '0.375rem' : '0.5rem',
    padding: isMobile ? '0.75rem' : '1rem',
    marginBottom: '1rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    border: '2px solid #E5E7EB'
  },

  politenessLevelCard: {
    position: 'relative',
    background: '#F9FAFB',
    border: '2px solid #E5E7EB',
    borderRadius: isMobile ? '0.375rem' : '0.5rem',
    padding: isMobile ? '0.75rem 0.75rem 0.75rem 2.5rem' : '1rem 1rem 1rem 3rem',
    marginBottom: '1rem'
  },

  // ===== TAB STYLES (if needed) =====
  tabContainer: {
    display: isMobile ? 'none' : 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #000000',
    paddingBottom: '0',
    overflowX: 'auto',
    flexWrap: 'wrap'
  },

  tab: {
    padding: '0.75rem 1.5rem',
    background: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    color: '#6B7280',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: '-2px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    whiteSpace: 'nowrap'
  },

  activeTab: {
    color: '#000000',
    borderBottom: '3px solid #000000',
    background: '#F9FAFB'
  },

  tableRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: '1rem',
    padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.25rem',
    alignItems: 'center',
    borderBottom: '1px solid #E5E7EB'
  },

  tableKey: {
    fontWeight: '500',
    textTransform: 'capitalize',
    color: '#000000',
    fontSize: isMobile ? '0.85rem' : '0.9rem',
  },

  tableValue: {
    textAlign: 'left',
    color: '#374151'
  }
};

  // Render sentence patterns (special case)
  const renderSentencePatterns = (category) => {
    if (category.visualType !== 'sentence-patterns') return null;

    return (
      <div>
        {category.rule && (
          <div style={customStyles.ruleCard}>
            <strong style={{ color: '#1e40af' }}>{t('grammar.content.rule')}</strong> {category.rule}
          </div>
        )}

        {category.patterns &&
          Object.entries(category.patterns).map(([key, patterns]) => (
            <div key={key} style={customStyles.patternBox}>
              <h4
                style={{
                  color: '#374151',
                  marginBottom: '0.75rem',
                  textTransform: 'capitalize',
                  fontWeight: '600'
                }}
              >
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </h4>
              {Array.isArray(patterns) &&
                patterns.map((pattern, idx) => (
                  <div key={idx} style={customStyles.exampleCard}>
                    <Target style={customStyles.exampleIcon} size={18} />
                    <div style={{ fontFamily: 'monospace' }}>{pattern}</div>
                  </div>
                ))}
            </div>
          ))}
      </div>
    );
  };
  const renderTable = (tableData, title) => {
    if (!tableData) return null;

    // Handle object-based tables (like formationTable)
    if (typeof tableData === 'object' && !Array.isArray(tableData)) {
      return (
        <div style={customStyles.tableContainer}>
          <h4 style={{ marginBottom: '1rem', color: '#374151', fontWeight: '600' }}>
            {title || t('grammar.content.formationTable')}
          </h4>
          {Object.entries(tableData).map(([key, value]) => (
            <div key={key} style={customStyles.detailCard}>
              <h5
                style={{
                  color: '#4b5563',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                  textTransform: 'capitalize'
                }}
              >
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </h5>
              {value.rule && (
                <p
                  style={{
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    color: '#1e40af',
                    fontWeight: '500'
                  }}
                >
                  {value.rule}
                </p>
              )}
              {value.examples &&
                value.examples.map((ex, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: '#f9fafb',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      marginBottom: '0.25rem',
                      fontFamily: 'monospace',
                      fontSize: '0.9rem'
                    }}
                  >
                    {ex}
                  </div>
                ))}
            </div>
          ))}
        </div>
      );
    }

    // Handle array-based tables
    if (Array.isArray(tableData) && tableData.length > 0) {
      const headers = Object.keys(tableData[0]); // Automatically get headers from the first object

      return (
        <div style={customStyles.tableContainer}>
          {title && <div style={customStyles.tableHeader}>{title}</div>}
          <table style={customStyles.table}>
            <thead>
              <tr>
                {headers.map(header => (
                  <th key={header} style={{...customStyles.tableCell, fontWeight: '600', background: '#f9fafb', textAlign: 'left'}}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                  {headers.map(header => (
                    <SmartTableCell key={`${idx}-${header}`} style={customStyles.tableCell}>
  {row[header]}
</SmartTableCell>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };
  const renderRulesDetail = (rulesDetail) => {
  if (!rulesDetail) return null;
  const formatKey = (key) => {
    const spaced = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  return (
    <div style={customStyles.detailGrid}>
      {Object.entries(rulesDetail).map(([key, value]) => (
        <div key={key} style={customStyles.detailCard}>
          <h5
            style={{
              color: '#4b5563',
              fontWeight: '600',
              marginBottom: '0.5rem',
            }}
          >
            {formatKey(key)}
          </h5>
          <SmartText as="p" style={{ fontSize: '0.9rem', color: '#374151' }}>{value}</SmartText>
        </div>
      ))}
    </div>
  );
};
  const renderCategoryContent = (category) => {
    if (!category) return null;
      const formatSubCategoryTitle = (key) => {
      const spaced = key.replace(/([A-Z])/g, ' $1');
      return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    };
    return (
      <div>
        {/* Description */}
        {category.description && (
          <p style={{ marginBottom: '1rem', color: '#4b5563', lineHeight: '1.6' }}>
            <SmartText>{category.description}</SmartText>
          </p>
        )}

        {/* Sentence Patterns (special visualization) */}
        {category.visualType === 'sentence-patterns' && renderSentencePatterns(category)}

        {/* --- ✅ CORRECTED ORDER --- */}
        {/* Main Rule is now rendered first. */}
    {category.rule && category.visualType !== 'sentence-patterns' && (
          <div style={customStyles.ruleCard}>
           <strong style={{ color: '#1e40af' }}>{t('grammar.content.rule')}</strong>{' '}
            <SmartText as="span">{category.rule}</SmartText>
          </div>
        )}
           {category.advancedConnectors && (
          <div>
            {/* Part 1: Advanced Connectors */}
            <div style={{ marginTop: '1.5rem' }}>
              <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>{t('grammar.content.advancedConnectors')}:</h5>
              {category.advancedConnectors.map((item, idx) => (
                <div key={idx} style={{...customStyles.modalCard, borderLeft: '4px solid #7e22ce'}}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <code style={{ ...customStyles.codeHighlight, background: '#f3e8ff', color: '#5b21b6', fontSize: '1.1rem' }}>{item.connector}</code>
                    <span style={{ fontStyle: 'italic', color: '#4b5563' }}>{item.meaning}</span>
                  </div>
                  <div style={{ color: '#374151', margin: '0.75rem 0', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                    <p style={{margin: '0 0 0.5rem 0'}}><strong>{t('grammar.content.function')}:</strong> <SmartText as="span">{item.function}</SmartText></p>
                    <p style={{margin: 0}}><strong>{t('grammar.content.rhetoricalUse')}:</strong> <SmartText as="span">{item.rhetoricalUse}</SmartText></p>
                  </div>
                  <div style={{ ...customStyles.exampleCard, padding: '0.75rem', margin: '0', background: '#f9fafb' }}>
                    <Target size={16} style={{ ...customStyles.exampleIcon, left: '0.75rem', top: '0.75rem' }} />
                    <div style={{ fontFamily: 'monospace', marginLeft: '1rem' }}>{item.example}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Emphasis Techniques */}
        {category.emphasisTechniques && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              {t('grammar.content.emphasisTechniques')}:
            </h5>
            <div style={{...customStyles.detailGrid}}>
              {category.emphasisTechniques.map((item, idx) => (
                <div key={idx} style={{...customStyles.detailCard, display: 'flex', flexDirection: 'column'}}>
                  <h5 style={{ color: '#4b5569', fontWeight: '600', marginBottom: '0.5rem' }}>
                    {item.technique}
                  </h5>
                  <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '1rem', flexGrow: 1 }}>
                    <strong>{t('grammar.content.effect')}:</strong> {item.effect}
                  </p>

                  {/* This part handles the first object type */}
                  {item.example && (
                    <div>
                      <div style={{...customStyles.exampleCard, padding: '0.75rem', background: '#f9fafb'}}>
                        <div style={{ fontFamily: 'monospace' }}>{item.example}</div>
                      </div>
                      <div style={{...customStyles.analysisCard, marginTop: '0.5rem', fontSize: '0.85rem'}}>
                        <strong>{t('grammar.content.analysis')}:</strong>{item.analysis}
                      </div>
                    </div>
                  )}

                  {/* This part handles the second object type */}
                  {item.basic && (
                    <div style={{...customStyles.patternBox, background: '#f8fafc'}}>
                      <p style={{margin: '0 0 0.5rem 0'}}><strong>{t('grammar.content.basic')}:</strong> <code style={customStyles.codeHighlight}>{item.basic}</code></p>
                      <p style={{margin: 0}}><strong>{t('grammar.content.advanced')}:</strong> <code style={{...customStyles.codeHighlight, background: '#1e3a8a'}}>{item.advanced}</code></p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Rules Detail now comes after the main rule. */}
        {category.rulesDetail && renderRulesDetail(category.rulesDetail)}
        {category.stepByStep && (
  <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
    {Object.entries(category.stepByStep).map(([key, step]) => (
      <div key={key} style={{...customStyles.subCategoryCard, flex: 1}}>
        <h4 style={{ color: '#111827', fontWeight: '600', marginBottom: '0.5rem' }}>
          <span style={{...customStyles.codeHighlight, background: '#4f46e5', marginRight: '0.5rem'}}>{key.replace('step', 'Step ')}:</span>
          {step.name}
        </h4>
        <p style={{ color: '#4b5563', marginBottom: '1rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
          {step.process}
        </p>
        <div style={{ fontSize: '0.9rem' }}>
          {(step.examples || step.questions || []).map((item, idx) => (
            <div key={idx} style={{...customStyles.exampleCard, background: '#f9fafb', padding: '0.5rem 1rem', marginBottom: '0.5rem'}}>
              <div style={{ fontFamily: 'monospace' }}>{item}</div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)}
   {category.comparison && (
  <div style={{ marginTop: '1.5rem' }}>
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>

      {/* Check for the Dative/Accusative comparison type */}
      {category.comparison.dativeExamples ? (
        <>
          {/* Dative Column */}
          <div style={{...customStyles.subCategoryCard, borderTop: '4px solid #0ea5e9'}}>
            <h5 style={{ color: '#0369a1', fontWeight: '600', marginBottom: '0.75rem' }}>
              {t('grammar.content.dativeExamples')}</h5>
            {category.comparison.dativeExamples.map((ex, idx) => (
              <div key={idx} style={{...customStyles.exampleCard, background: '#f9fafb', padding: '0.5rem 1rem', marginBottom: '0.5rem'}}>
                <SmartText as="div" style={{ fontFamily: 'monospace' }}>{ex}</SmartText>
              </div>
            ))}
          </div>

          {/* Accusative Column */}
          <div style={{...customStyles.subCategoryCard, borderTop: '4px solid #8b5cf6'}}>
            <h5 style={{ color: '#5b21b6', fontWeight: '600', marginBottom: '0.75rem' }}>
              {t('grammar.content.accusativeExamples')}</h5>
            {category.comparison.accusativeExamples.map((ex, idx) => (
              <div key={idx} style={{...customStyles.exampleCard, background: '#f9fafb', padding: '0.5rem 1rem', marginBottom: '0.5rem'}}>
                <SmartText as="div" style={{ fontFamily: 'monospace' }}>{ex}</SmartText>
              </div>
            ))}
          </div>
        </>
           ) : category.comparison.active ? (
        <>
          {/* Active Column */}
          <div style={{...customStyles.subCategoryCard, borderTop: '4px solid #f87171'}}>
            <h5 style={{ color: '#b91c1c', fontWeight: '600', marginBottom: '0.75rem' }}>{t('grammar.content.active')}</h5>
            {(category.comparison.active || []).map((ex, idx) => (
              <div key={idx} style={{...customStyles.exampleCard, background: '#fee2e2', border: '1px solid #fecaca', padding: '0.5rem 1rem', marginBottom: '0.5rem'}}>
                <SmartText as="div" style={{ fontFamily: 'monospace' }}>{ex}</SmartText>
              </div>
            ))}
          </div>

          {/* Passive Column */}
          <div style={{...customStyles.subCategoryCard, borderTop: '4px solid #4ade80'}}>
            <h5 style={{ color: '#15803d', fontWeight: '600', marginBottom: '0.75rem' }}>{t('grammar.content.passive')}</h5>
            {(category.comparison.passive || []).map((ex, idx) => (
              <div key={idx} style={{...customStyles.exampleCard, background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '0.5rem 1rem', marginBottom: '0.5rem'}}>
                <SmartText as="div" style={{ fontFamily: 'monospace' }}>{ex}</SmartText>
              </div>
            ))}
          </div>
        </>

      ) : (
        /* Otherwise, render the Direct/Polite comparison type */
        <>
           {/* Direct Column */}
  <div style={{...customStyles.subCategoryCard, borderTop: '4px solid #f87171'}}>
    <h5 style={{ color: '#b91c1c', fontWeight: '600', marginBottom: '0.75rem' }}>
      <SmartText>{category.comparison.direct?.title}</SmartText>
    </h5>
    {/* Add check before mapping items as well */}
    {category.comparison.direct?.items?.map((ex, idx) => (
      <div key={idx} style={{...customStyles.exampleCard, background: '#fee2e2', border: '1px solid #fecaca', padding: '0.5rem 1rem', marginBottom: '0.5rem'}}>
        <SmartText as="div" style={{ fontFamily: 'monospace' }}>{ex}</SmartText>
      </div>
    ))}
  </div>

  {/* Polite Column */}
  <div style={{...customStyles.subCategoryCard, borderTop: '4px solid #4ade80'}}>
    <h5 style={{ color: '#15803d', fontWeight: '600', marginBottom: '0.75rem' }}>
      <SmartText>{category.comparison.polite?.title}</SmartText>
    </h5>
    {/* Add check before mapping items as well */}
    {category.comparison.polite?.items?.map((ex, idx) => (
      <div key={idx} style={{...customStyles.exampleCard, background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '0.5rem 1rem', marginBottom: '0.5rem'}}>
        <SmartText as="div" style={{ fontFamily: 'monospace' }}>{ex}</SmartText>
      </div>
    ))}
  </div>
</>
      )}
    </div>
  </div>
)}
        {category.pattern && (
  <div style={customStyles.ruleCard}>
    <strong>{t('grammar.content.structure')}:</strong>  <code style={customStyles.codeHighlight}>{category.pattern}</code>
  </div>
)}

{/* Renders Price Examples */}
{category.priceExamples && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>{t('grammar.content.priceExamples')}:</h5>
    {category.priceExamples.map((item, idx) => (
      <div key={idx} style={{...customStyles.exampleCard, background: '#f8fafc', padding: '1rem', marginBottom: '0.5rem'}}>
        <strong style={{...customStyles.codeHighlight, background: '#e0e7ff', color: '#3730a3'}}>{item.price}</strong>
        <p style={{fontFamily: 'monospace', margin: '0.5rem 0'}}>→ {item.formal}</p>
        <p style={{fontSize: '0.9rem', color: '#6b7280', margin: 0, fontStyle: 'italic'}}>({item.translation})</p>
      </div>
    ))}
  </div>
)}

{/* Renders Measurement Examples */}
{category.measurementExamples && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>{t('grammar.content.measurementExamples')}:</h5>
    {category.measurementExamples.map((item, idx) => (
      <div key={idx} style={{...customStyles.exampleCard, background: '#f8fafc', padding: '1rem', marginBottom: '0.5rem'}}>
        <strong style={{...customStyles.codeHighlight, background: '#e0e7ff', color: '#3730a3'}}>{item.measurement}</strong>
        <p style={{fontFamily: 'monospace', margin: '0.5rem 0'}}>→ {item.formal}</p>
        <p style={{fontSize: '0.9rem', color: '#6b7280', margin: 0, fontStyle: 'italic'}}>({item.translation})</p>
      </div>
    ))}
  </div>
)}
        {category.politenessLevels && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
     {t('grammar.content.politenessLevels')}:</h5>
    {category.politenessLevels.map((item, idx) => (
      <div key={idx} style={customStyles.politenessLevelCard}>
        <div style={{
            position: 'absolute',
            left: '1rem',
            top: '1rem',
            background: '#eef2ff',
            color: '#4338ca',
            fontWeight: '600',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.8rem'
        }}>
          {idx + 1}
        </div>
        <strong style={{ display: 'block', color: '#111827', marginBottom: '0.25rem' }}>
          {item.level}
        </strong>
        <p style={{ fontFamily: 'monospace', margin: '0 0 0.25rem 0' }}>
          {item.example}
        </p>
        <p style={{ fontStyle: 'italic', fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>
          {item.translation}
        </p>
      </div>
    ))}
  </div>
)}
        {/* ✅ ADD THIS BLOCK to render the 'verbList' array */}
{category.verbList && (
  <div style={{ marginTop: '1.5rem' }}>
    {category.verbList.map((item, idx) => (
      <div key={idx} style={{...customStyles.modalCard, marginBottom: '1rem'}}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <code style={{ ...customStyles.codeHighlight, background: '#dbeafe', color: '#1e40af' }}>{item.verb}</code>
          <span style={{ fontStyle: 'italic', color: '#4b5563' }}>{item.meaning}</span>
        </div>
        <div style={{ ...customStyles.exampleCard, padding: '0.75rem', margin: '0.75rem 0 0 0', background: '#f9fafb' }}>
          <Target size={16} style={{ ...customStyles.exampleIcon, left: '0.75rem', top: '0.75rem' }} />
          <div style={{ fontFamily: 'monospace', marginLeft: '1rem' }}>{item.example}</div>
        </div>
        {item.note && (
          <p style={{fontSize: '0.9rem', color: '#374151', margin: '0.75rem 0 0 0', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb'}}>
           <strong>{t('grammar.content.note')}:</strong>{item.note}
          </p>
        )}
      </div>
    ))}
  </div>
)}
        {category.prepositionMeanings && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
      {t('grammar.content.prepositionMeanings')}:</h5>
    {renderRulesDetail(category.prepositionMeanings)}
  </div>
)}
        {category.prepositionGroups && (
  <div style={{ marginTop: '1.5rem' }}>
    {category.prepositionGroups.map((group, idx) => (
      <div key={idx} style={{...customStyles.subCategoryCard, marginBottom: '1.5rem'}}>
        <h4 style={{ color: '#111827', fontWeight: '600', marginBottom: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' }}>
         <h4>{t('grammar.content.verbsWith', { preposition: group.preposition })}</h4> <code style={{...customStyles.codeHighlight, fontSize: '1rem'}}>{group.preposition}</code>
        </h4>
        {group.verbs.map(item => (
          <div key={item.verb} style={{marginBottom: '1rem'}}>
            <strong style={{color: '#4338ca'}}>{item.verb}</strong>
            <div style={{...customStyles.exampleCard, background: '#f9fafb', padding: '0.75rem', marginTop: '0.25rem'}}>
              <Target size={16} style={{...customStyles.exampleIcon, left: '0.75rem', top: '0.75rem'}}/>
              <div style={{fontFamily: 'monospace', marginLeft: '1rem'}}>
                {item.example}
                <em style={{color: '#6b7280', display: 'block'}}>({item.translation})</em>
              </div>
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
)}
        {category.prepositionList && (
  <div style={{ marginTop: '1.5rem' }}>
    {category.prepositionList.map((item, idx) => (
      <div key={idx} style={{...customStyles.subCategoryCard, marginBottom: '1rem'}}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <code style={{ ...customStyles.codeHighlight, background: '#1e3a8a', fontSize: '1.1rem' }}>
            {item.preposition}
          </code>
          <span style={{ fontStyle: 'italic', color: '#4b5563', textAlign: 'right' }}>
            {item.meaning}
          </span>
        </div>
        <div style={{...customStyles.exampleCard, background: '#f9fafb', padding: '0.75rem'}}>
          <Target size={16} style={{...customStyles.exampleIcon, left: '0.75rem', top: '0.75rem'}}/>
          <div style={{fontFamily: 'monospace', marginLeft: '1rem'}}>{item.example}</div>
        </div>
        <p style={{fontSize: '0.9rem', color: '#374151', margin: '0.75rem 0 0 0', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb'}}>
          <strong>{t('grammar.content.note')}:</strong>{item.note}
        </p>
      </div>
    ))}
  </div>
)}
        {category.commonPairs && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
      {t('grammar.content.verbPairs')}</h5>
    {category.commonPairs.map((pair, idx) => (
      <div key={idx} style={{...customStyles.subCategoryCard, marginBottom: '1rem'}}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
          <div>
            <span style={{ fontSize: '0.8rem', color: '#4f46e5', fontWeight: '600' }}>{t('grammar.content.action')}</span>
            <p style={{ margin: '0.25rem 0', fontWeight: '500' }}>{pair.action}</p>
          </div>
          <div>
            <span style={{ fontSize: '0.8rem', color: '#0369a1', fontWeight: '600' }}>{t('grammar.content.state')}</span>
            <p style={{ margin: '0.25rem 0', fontWeight: '500' }}>{pair.state}</p>
          </div>
        </div>
        <div style={{...customStyles.exampleCard, background: '#f9fafb', padding: '0.75rem', border: '1px solid #e5e7eb'}}>
          <Target size={16} style={{...customStyles.exampleIcon, left: '0.75rem', top: '0.75rem'}}/>
          <div style={{fontFamily: 'monospace', marginLeft: '1rem'}}>{pair.example}</div>
        </div>
      </div>
    ))}
  </div>
)}
        {category.commonPrefixes && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ marginBottom: '0.75rem', color: '#374151', fontWeight: '600' }}>
      {t('grammar.content.commonPrefixes')}:</h5>
    <div style={customStyles.patternBox}>
      {category.commonPrefixes.map((prefix, idx) => (
        <p key={idx} style={{ margin: '0 0 0.5rem 0', color: '#4b5563' }}>{prefix}</p>
      ))}
    </div>
  </div>
)}
        {category.conjunctions && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
      {t('grammar.content.conjunctionsDetail')}:</h5>
    {Object.entries(category.conjunctions).map(([conj, details]) => (
      <div key={conj} style={{...customStyles.subCategoryCard, marginBottom: '1rem'}}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <code style={{ ...customStyles.codeHighlight, background: '#dbeafe', color: '#1e40af', fontSize: '1.1rem' }}>
            {conj}
          </code>
          <span style={{ fontStyle: 'italic', color: '#4b5563' }}>
            {details.meaning}
          </span>
        </div>
        <p style={{ color: '#374151', marginBottom: '1rem' }}>
          <strong>Usage:</strong> {details.usage}
        </p>
        <div>
          {details.examples.map((ex, idx) => (
            <div key={idx} style={{...customStyles.exampleCard, padding: '0.75rem', marginBottom: '0.5rem', background: '#f9fafb'}}>
              <Target size={16} style={{...customStyles.exampleIcon, left: '0.75rem', top: '0.75rem'}}/>
              <div style={{fontFamily: 'monospace', marginLeft: '1rem'}}>{ex}</div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)}
        {/* Passive Alternative Types */}
        {category.alternativeTypes && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              {t('grammar.content.passiveAlternatives')}:</h5>
            {category.alternativeTypes.map((item, idx) => (
              <div key={idx} style={{...customStyles.subCategoryCard, marginBottom: '1.5rem', borderLeft: '4px solid #f97316'}}>
                {/* --- Main Info for Each Type --- */}
                <strong style={{ display: 'block', color: '#9a3412', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                  {item.type}
                </strong>
                 <p style={{ color: '#4b5563', margin: '0 0 0.5rem 0' }}><SmartText>{item.meaning}</SmartText></p>
                {item.structure && (
                  <p style={{ margin: '0 0 1rem 0' }}>
                    <strong>{t('grammar.content.structure')}:</strong>  <code style={customStyles.codeHighlight}><SmartText as="span">{item.structure}</SmartText></code>
                  </p>
                )}

                {/* --- This part handles the FIRST object type (where example is a string) --- */}
                {item.example && typeof item.example === 'string' && (
                    <div style={{...customStyles.modalCard, padding: '1rem'}}>
                        <div style={{...customStyles.exampleCard, fontFamily: 'monospace', padding: '0.5rem', background: '#f9fafb'}}>
                            {item.example}
                        </div>
                        <p style={{fontSize: '0.9rem', color: '#4b5563', margin: '0.5rem 0'}}>{t('grammar.content.insteadOf')}: <em>"{item.instead_of}"</em></p>
                        <div style={{...customStyles.analysisCard, background: '#fff7ed', borderLeftColor: '#fb923c'}}>
                            <strong>{t('grammar.content.effect')}:</strong> <SmartText>{item.effect}</SmartText>
                        </div>
                    </div>
                )}

                {/* --- This part handles the OTHER types (where examples is an array) --- */}
                {item.examples && Array.isArray(item.examples) && (
                  <div>
                    {item.examples.map((ex, exIdx) => (
                      <div key={exIdx} style={{...customStyles.modalCard, padding: '1rem', marginBottom: '0.5rem'}}>
                        {ex.adjective && (
                            <p style={{margin: '0 0 0.5rem 0'}}>Adjective: <code style={{...customStyles.codeHighlight, background: '#ffedd5', color: '#9a3412'}}>{ex.adjective}</code></p>
                        )}
                        <div style={{...customStyles.exampleCard, fontFamily: 'monospace', padding: '0.5rem', background: '#f9fafb'}}>
                          {ex.sentence}
                        </div>
                        <p style={{fontSize: '0.9rem', color: '#4b5563', margin: '0.5rem 0'}}>
                            {ex.meaning && <span>(<SmartText>{ex.meaning}</SmartText>)<br/></span>}
                            {t('grammar.content.insteadOf')}: <em>"<SmartText>{ex.instead_of}</SmartText>"</em>
                        </p>
                        {ex.note && (
                            <div style={{...customStyles.noteCard, background: '#fffbeb', borderLeftColor: '#f59e0b', color: '#b45309'}}>
                                <Info size={20} style={{ flexShrink: 0 }} />
                                <div><SmartText>{ex.note}</SmartText></div>
                            </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {category.conjunctionCategories && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
      {t('grammar.content.conjunctionTypes')}:</h5>
    <div style={{ marginTop: '1rem' }}> {/* No more detailGrid style */}
  {Object.entries(category.conjunctionCategories).map(([key, list]) => (
    <div key={key} style={customStyles.conjunctionCategoryCard}> {/* Use the new card style */}
      <h5 style={{ textTransform: 'capitalize', color: '#4338ca', fontWeight: '600', marginBottom: '0.5rem' }}>
        {key}
      </h5>
      <ul style={{ paddingLeft: '1.25rem', margin: 0, color: '#374151', fontSize: '0.9rem' }}>
        {list.map((item, idx) => (
          <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>
        ))}
      </ul>
    </div>
  ))}
</div>
  </div>
)}
       {category.categories && (
          <div style={{ marginTop: '1.5rem' }}>
            {Object.entries(category.categories).map(([key, subCat]) => (
              <div key={key} style={customStyles.subCategoryCard}>
                <h4 style={{ color: '#111827', fontWeight: '600', marginBottom: '0.5rem' }}>
                  {formatSubCategoryTitle(key)}
                </h4>
                <p style={{ color: '#4b5563', marginBottom: '1rem' }}>{subCat.explanation}</p>
                {subCat.verbs && (
                  <div style={{ marginBottom: '1rem' }}>
                    {subCat.verbs.map(verb => (
                      <code key={verb} style={{...customStyles.codeHighlight, marginRight: '0.5rem', marginBottom: '0.5rem', display: 'inline-block'}}>{verb}</code>
                    ))}
                  </div>
                )}
                {subCat.examples && subCat.examples.map((ex, idx) => (
                  <div key={idx} style={{...customStyles.exampleCard, paddingLeft: '1rem', paddingRight: '1rem', marginBottom: '0.5rem'}}>
                     <div style={{ fontFamily: 'monospace' }}>{ex}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* ✅ NEW: RENDER LOGIC FOR THE 'mainCategories' OBJECT */}
        {category.mainCategories && (
          <div style={{ marginTop: '1.5rem' }}>
            {Object.entries(category.mainCategories).map(([key, subCat]) => (
              <div key={key} style={customStyles.subCategoryCard}>
                <h4 style={{ color: '#111827', fontWeight: '600', marginBottom: '0.5rem' }}>
                  {formatSubCategoryTitle(key)}
                </h4>
                <p style={{ color: '#4b5563', marginBottom: '1rem' }}>{subCat.explanation}</p>
                 {subCat.examples && subCat.examples.map((ex, idx) => (
                  <div key={idx} style={{...customStyles.exampleCard, paddingLeft: '1rem', paddingRight: '1rem', marginBottom: '0.5rem'}}>
                     <div style={{ fontFamily: 'monospace' }}>{ex}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {category.registerMatrix && (
          <div style={customStyles.tableContainer}>
             <div style={customStyles.tableHeader}>{t('grammar.content.registerMatrix')}</div>
            <table style={customStyles.table}>
              <thead>
                <tr>
                  <th style={{...customStyles.tableCell, fontWeight: '700', background: '#eef2ff', color: '#1e40af', width: '15%', textAlign: 'left'}}>{t('grammar.content.context')}</th>
                  <th style={{...customStyles.tableCell, fontWeight: '700', background: '#eef2ff', color: '#1e40af', width: '20%', textAlign: 'left'}}>{t('grammar.content.modalParticles')}</th>
                  <th style={{...customStyles.tableCell, fontWeight: '700', background: '#eef2ff', color: '#1e40af', width: '25%', textAlign: 'left'}}>{t('grammar.content.nvv')}</th>
                  <th style={{...customStyles.tableCell, fontWeight: '700', background: '#eef2ff', color: '#1e40af', width: '20%', textAlign: 'left'}}>{t('grammar.content.prepositions')}</th>
                  <th style={{...customStyles.tableCell, fontWeight: '700', background: '#eef2ff', color: '#1e40af', width: '20%', textAlign: 'left'}}>{t('grammar.content.overallTone')}</th>
                </tr>
              </thead>
              <tbody>
                {category.registerMatrix.map((row, idx) => (
                  <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                    <SmartTableCell style={{...customStyles.tableCell, fontWeight: '600'}}>
                    {row.context}
                  </SmartTableCell>
                    <SmartTableCell style={customStyles.tableCell}>
                    {row.modalParticles}
                  </SmartTableCell>
                    <SmartTableCell style={customStyles.tableCell}>
                    {row.nvv}
                  </SmartTableCell>
                    <SmartTableCell style={customStyles.tableCell}>
                    {row.prepositions}
                  </SmartTableCell>
                    <SmartTableCell style={customStyles.tableCell}>
                    {row.overallTone}
                  </SmartTableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
         {category.objectHierarchy && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
              {t('grammar.content.objectHierarchy')}
            </h5>
            <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
              {category.objectHierarchy.map((item, idx) => (
                <div key={idx} style={{...customStyles.subCategoryCard, borderLeft: '4px solid #8b5cf6'}}>

                  <strong style={{ display: 'block', color: '#5b21b6', fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                    <SmartText>{item.rule}</SmartText>
                  </strong>

                  <p style={{ color: '#4b5563', margin: '0 0 1rem 0', fontSize: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem'}}>
                    <strong style={{ color: '#6d28d9' }}>{t('grammar.content.principle')}:</strong><SmartText>{item.principle}</SmartText>
                  </p>

                  {item.examples.map((ex, exIdx) => (
                    <div key={exIdx} style={{...customStyles.patternBox, background: '#f5f3ff', marginBottom: '0.75rem'}}>
                      <span style={{ fontSize: '0.8rem', color: '#6d28d9', fontWeight: '600', display: 'block' }}>{t('grammar.content.order')}: {ex.structure}</span>
                      <p style={{ fontFamily: 'monospace', margin: '0.25rem 0', fontSize: '0.95rem' }}><SmartText>{ex.example}</SmartText></p>
                      <div style={{...customStyles.analysisCard, background: '#eef2ff', borderLeftColor: '#4f46e5', marginTop: '0.5rem', fontSize: '0.85rem'}}>
                         <SmartText>{ex.analysis}</SmartText>
                      </div>
                    </div>
                  ))}

                  {item.note && (
                    <div style={{...customStyles.tipCard, background: '#fef3c7', color: '#92400e', marginTop: '1rem'}}>
                      <Info size={20} style={{ flexShrink: 0 }} />
                      <div><SmartText>{item.note}</SmartText></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {category.esFunctions && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              {t('grammar.content.esFunctions')}
            </h5>
            <div style={customStyles.detailGrid}>
              {category.esFunctions.map((item, idx) => (
                <div key={idx} style={{...customStyles.detailCard, borderTop: '4px solid #10b981', display: 'flex', flexDirection: 'column'}}>

                  <strong style={{ display: 'block', color: '#047857', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                    <SmartText>{item.function}</SmartText> {/* ADDED SmartText */}
                  </strong>
                  <p style={{ color: '#4b5563', margin: '0 0 1rem 0', fontSize: '0.9rem', flexGrow: 1 }}>
                    <SmartText>{item.description}</SmartText> {/* ADDED SmartText */}
                  </p>

                  {(item.example || (item.examples && item.examples.length)) && (
                    <div style={{ marginBottom: '1rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem'}}>
                      <span style={{ fontSize: '0.8rem', color: '#0f766e', fontWeight: '600', display: 'block', marginBottom: '0.5rem' }}>{t('grammar.content.examples')}:</span>

                      {/* Handle the single-string example */}
                      {item.example && (
                        <div style={{...customStyles.exampleCard, fontFamily: 'monospace', padding: '0.5rem', background: '#f0fdf9', border: '1px solid #ccfbf1', marginBottom: '0.5rem', paddingLeft: '1rem'}}>
                          <SmartText>{item.example}</SmartText> {/* ADDED SmartText */}
                          <div style={{...customStyles.analysisCard, background: '#e0e7ff', borderLeftColor: '#4f46e5', marginTop: '0.5rem', fontSize: '0.85rem'}}>
                            <SmartText>{item.analysis}</SmartText> {/* ADDED SmartText */}
                          </div>
                        </div>
                      )}

                      {/* Handle the array of nested examples */}
                      {item.examples && item.examples.map((ex, exIdx) => (
                        <div key={exIdx} style={{...customStyles.exampleCard, fontFamily: 'monospace', padding: '0.5rem', background: '#f0fdf9', border: '1px solid #ccfbf1', marginBottom: '0.5rem', paddingLeft: '1rem'}}>
                          <SmartText>{ex.sentence}</SmartText> {/* ADDED SmartText */}
                          <div style={{...customStyles.analysisCard, background: '#f0f9ff', borderLeftColor: '#0ea5e9', marginTop: '0.5rem', fontSize: '0.85rem'}}>
                            <SmartText><strong>{t('grammar.content.analysis')}:</strong> {ex.analysis}</SmartText> {/* ADDED SmartText */}
                            {ex.transformation && <p style={{margin: '0.25rem 0 0 0'}}>**{t('grammar.content.transformation')}:** <SmartText>{ex.transformation}</SmartText></p>} {/* ADDED SmartText */}
                            {ex.note && <p style={{margin: '0.25rem 0 0 0'}}>**{t('grammar.content.note')}:** <SmartText>{ex.note}</SmartText></p>} {/* ADDED SmartText */}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {item.note && typeof item.note === 'string' && (
                    <div style={{...customStyles.tipCard, marginTop: 'auto'}}>
                      <Lightbulb size={20} color="#f59e0b" />
                      <div><SmartText>{item.note}</SmartText></div> {/* ADDED SmartText */}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {category.worCompounds && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
              WO(R)- {t('grammar.content.worCompounds')}</h5>
            <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
              {category.worCompounds.map((item, idx) => (
                <div key={idx} style={{...customStyles.subCategoryCard, borderLeft: '4px solid #10b981'}}>

                  <strong style={{ display: 'block', color: '#047857', fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                    <SmartText>{item.function}</SmartText>
                  </strong>

                  <p style={{ color: '#4b5563', margin: '0 0 1rem 0', fontSize: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem'}}>
                    <strong style={{ color: '#0f766e' }}>{t('grammar.content.principle')}:</strong><SmartText>{item.principle}</SmartText>
                    <br/>
                    <strong style={{ color: '#0f766e' }}>{t('grammar.content.rule')}:</strong> <SmartText>{item.rule}</SmartText>
                  </p>

                  {item.examples.map((ex, exIdx) => (
                    <div key={exIdx} style={{...customStyles.patternBox, background: '#f0fdf9', marginBottom: '0.75rem'}}>
                      <span style={{ fontSize: '0.8rem', color: '#0f766e', fontWeight: '600', display: 'block' }}>{t('grammar.content.context')}: {ex.context || t('grammar.content.general')}</span>
                      <p style={{ fontFamily: 'monospace', margin: '0.25rem 0 0 0', fontSize: '0.95rem' }}><SmartText>{ex.sentence}</SmartText></p>
                      <div style={{...customStyles.analysisCard, background: '#e0e7ff', borderLeftColor: '#4f46e5', marginTop: '0.5rem', fontSize: '0.85rem'}}>
                         <SmartText>{ex.analysis}</SmartText>
                      </div>
                      {ex.note && (
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#4b5563'}}>**Note:** <SmartText>{ex.note}</SmartText></p>
                      )}
                    </div>
                  ))}

                </div>
              ))}
            </div>
          </div>
        )}

        {/* ✅ NEW: RENDER LOGIC FOR 'formationPattern' */}
        {category.formationPattern && (
          <div style={customStyles.stepBox}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>{t('grammar.content.formationPattern')}:</h5>
            {Object.entries(category.formationPattern).map(([key, value]) => (
              <p key={key} style={{ margin: '0 0 0.5rem 0', color: '#4b5563' }}>
                <strong style={{textTransform: 'capitalize'}}>{key.replace('step', 'Step ')}:</strong> {value}
              </p>
            ))}
          </div>
        )}

        {/* ✅ NEW: RENDER LOGIC FOR 'modalTable' */}
        {category.modalTable && (
          <div style={{ marginTop: '1.5rem' }}>
             <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>{t('grammar.content.modalVerbForms')}:</h5>
            {Object.entries(category.modalTable).map(([verb, details]) => (
              <div key={verb} style={customStyles.modalCard}>
                <strong style={{textTransform: 'capitalize', color: '#1e3a8a'}}>{verb} → {details.forms}</strong>
                <div style={{...customStyles.exampleCard, padding: '0.75rem', margin: '0.75rem 0 0 0', background: '#f9fafb'}}>
                  <Target size={16} style={{...customStyles.exampleIcon, left: '0.75rem', top: '0.75rem'}}/>
                  <div style={{fontFamily: 'monospace', marginLeft: '1rem'}}>{details.example}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {category.contactLogic && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
             {t('grammar.content.spatialLogic')}:</h5>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              {category.contactLogic.map((prepositionGroup, idx) => (
                <div key={idx} style={{...customStyles.subCategoryCard, borderTop: `4px solid ${idx % 2 === 0 ? '#f59e0b' : '#3b82f6'}`}}>

                  <strong style={{ display: 'block', color: `${idx % 2 === 0 ? '#b45309' : '#1e40af'}`, fontSize: '1.4rem', marginBottom: '0.5rem' }}>
                    <SmartText>{prepositionGroup.preposition}</SmartText>
                  </strong>

                  <p style={{ color: '#4b5563', margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
                    <strong style={{ color: '#374151' }}>{t('grammar.content.concept')}:</strong> <SmartText>{prepositionGroup.concept}</SmartText>
                  </p>
                  <p style={{ color: '#4b5563', margin: '0 0 1rem 0', fontSize: '0.9rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
                    <strong style={{ color: '#374151' }}>{t('grammar.content.contactType')}:</strong> <SmartText>{prepositionGroup.contactType}</SmartText>
                  </p>

                  <h6 style={{ color: '#374151', fontWeight: '600', marginBottom: '0.75rem' }}>{t('grammar.content.examples')}::</h6>
                  {prepositionGroup.examples.map((ex, exIdx) => (
                    <div key={exIdx} style={{...customStyles.exampleCard, padding: '0.75rem', marginBottom: '0.5rem', background: '#f8fafc'}}>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.9rem', marginBottom: '0.5rem' }}><SmartText>{ex.sentence}</SmartText></div>
                      <div style={{...customStyles.analysisCard, background: '#eef2ff', borderLeftColor: '#4f46e5', fontSize: '0.85rem'}}>
                         <strong>{t('grammar.content.caseFunction')}</strong> <SmartText>{ex.case}</SmartText> (<SmartText>{ex.analysis}</SmartText>)
                      </div>
                    </div>
                  ))}

                </div>
              ))}
            </div>
          </div>
        )}

        {/* ✅ NEW: RENDER LOGIC FOR 'commonIrregulars' */}
        {category.commonIrregulars && (
           <div style={{ marginTop: '1.5rem' }}>
             <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>{t('grammar.content.commonIrregulars')}:</h5>
            <div style={customStyles.irregularGrid}>
              {Object.entries(category.commonIrregulars).map(([infinitive, pastForm]) => (
                <div key={infinitive} style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                  <code style={{...customStyles.codeHighlight, background: '#dbeafe', color: '#1e40af'}}>{infinitive}</code>
                  <span>→</span>
                  <code style={{...customStyles.codeHighlight}}>{pastForm}</code>
                </div>
              ))}
            </div>
           </div>
        )}

        {/* ✅ MODIFIED: Handle the special structure of the 'formationTable' for regular verbs */}
        {category.formationTable && category.formationTable.pattern && (
           <div style={{ marginTop: '1.5rem' }}>
              <div style={customStyles.ruleCard}>
                <strong style={{ color: '#1e40af' }}>{t('grammar.content.pattern')}:</strong> {category.formationTable.pattern}
              </div>
              <div style={{marginTop: '1rem'}}>
                 {category.formationTable.examples.map((ex, idx) => (
                    <div key={idx} style={{...customStyles.exampleCard, paddingLeft: '1rem', paddingRight: '1rem', marginBottom: '0.5rem'}}>
                      <div style={{ fontFamily: 'monospace' }}>{ex}</div>
                    </div>
                 ))}
              </div>
           </div>
        )}

        {category.conjugationTable && renderTable(category.conjugationTable, t('grammar.content.conjugation'))}
        {category.declensionTable && renderTable(category.declensionTable)}
        {category.examplesTable && renderTable(category.examplesTable, t('grammar.content.examplesAndCases'))}


        {/* Usage */}
        {category.usage && (
          <div style={customStyles.usageCard}>
          <strong>{t('grammar.content.usage')}</strong><SmartText>{category.usage}</SmartText>
          </div>
        )}

        {/* Connectors List */}
        {category.connectors && Array.isArray(category.connectors) && (
          <div style={{ marginTop: '1rem' }}>
            <strong style={{ color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
              {t('grammar.content.connectors')}
            </strong>
            <div>
              {category.connectors.map((connector, index) => (
                <code
                  key={index}
                  style={{
                    ...customStyles.codeHighlight,
                    marginRight: '0.5rem',
                    marginBottom: '0.5rem',
                    display: 'inline-block'
                  }}
                >
                  {connector}
                </code>
              ))}
            </div>
          </div>
        )}
        {category.commonVerbs && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
      {t('grammar.content.commonVerbsCase')}:</h5>
    <div>
      {category.commonVerbs.map(verb => (
        <code key={verb} style={{...customStyles.codeHighlight, marginRight: '0.5rem', marginBottom: '0.5rem', display: 'inline-block'}}>
          {verb}
        </code>
      ))}
    </div>
  </div>
)}
        {/* Comprehensive Particle Guide */}
{category.comprehensiveParticleGuide && (
  <div style={{ marginTop: '1.5rem' }}>
    {category.comprehensiveParticleGuide.map((item, idx) => (
      <div
        key={idx}
        style={{
          ...customStyles.subCategoryCard,
          marginBottom: '1.5rem',
          borderLeft: '4px solid #8b5cf6'
        }}
      >
        {/* header row: particle + coreMeaning */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '0.5rem'
          }}
        >
          <code
            style={{
              ...customStyles.codeHighlight,
              background: '#f5f3ff',
              color: '#5b21b6',
              fontSize: '1.5rem'
            }}
          >
            {item.particle}
          </code>
          <strong style={{ color: '#6d28d9' }}><SmartText as="span">{item.coreMeaning}</SmartText></strong>
        </div>

        {/* typical position badge */}
        {item.typicalPosition && (
          <div
            style={{
              display: 'inline-block',
              marginBottom: '0.75rem',
              padding: '0.25rem 0.5rem',
              background: '#e0e7ff',
              color: '#4338ca',
              borderRadius: '0.375rem',
              fontSize: '0.75rem',
              fontWeight: 600
            }}
          >
            Position: <SmartText as="span">{item.typicalPosition}</SmartText>
          </div>
        )}

        {/* explanation paragraph */}
        <p
          style={{
            color: '#4b5563',
            margin: '0 0 1rem 0',
            borderTop: '1px solid #e5e7eb',
            paddingTop: '1rem'
          }}
        >
          <SmartText>{item.explanation}</SmartText>
        </p>

        {/* examples list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {item.examples.map((ex, exIdx) => (
            <div
              key={exIdx}
              style={{
                ...customStyles.exampleCard,
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                padding: '0.75rem',
                position: 'relative'
              }}
            >
              <Target
                size={16}
                style={{
                  ...customStyles.exampleIcon,
                  left: '0.75rem',
                  top: '0.75rem'
                }}
              />
              <div style={{ fontFamily: 'monospace', marginLeft: '1rem' }}>
                <SmartText as="span">{ex.sentence}</SmartText>
                <em
                  style={{
                    color: '#6b7280',
                    display: 'block',
                    marginTop: '0.25rem'
                  }}
                >
                  → <SmartText as="span">{ex.translation}</SmartText>
                </em>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)}
          {category.verbNounTransformation && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Functional Verb (NVV) Concepts:
            </h5>
            <div style={customStyles.detailGrid}>
              {category.verbNounTransformation.map((item, idx) => (
                <div key={idx} style={{...customStyles.detailCard, borderTop: '4px solid #10b981'}}>
                  <strong style={{ display: 'block', color: '#047857', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                    <SmartText as="span">{item.concept}</SmartText>
                  </strong>
                  <p style={{ color: '#4b5563', margin: '0 0 1rem 0', fontSize: '0.9rem', flexGrow: 1 }}>
                    <SmartText>{item.explanation}</SmartText>
                  </p>

                  {/* Transformation Example Box */}
                  <div style={{...customStyles.patternBox, background: '#f0fdf4', border: '1px solid #a7f3d0'}}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <span style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: '600' }}>SIMPLE VERB:</span>
                      <p style={{ fontFamily: 'monospace', margin: '0.25rem 0', fontSize: '0.9rem' }}><SmartText as="span">{item.example.simple || item.example.basic}</SmartText></p>
                    </div>
                    {item.example.nvv && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.8rem', color: '#1d4ed8', fontWeight: '600' }}>NVV (Formal):</span>
                        <p style={{ fontFamily: 'monospace', margin: '0.25rem 0', fontSize: '0.9rem' }}><SmartText as="span">{item.example.nvv || item.example.precise}</SmartText></p>
                      </div>
                    )}
                    <div style={{ marginTop: '0.5rem', borderTop: '1px dashed #d1fae5', paddingTop: '0.5rem'}}>
                      <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '600' }}>KEY INSIGHT:</span>
                      <p style={{ fontFamily: 'monospace', margin: '0.25rem 0', fontSize: '0.9rem' }}><SmartText as="span">{item.example.functionalVerb || item.example.advantage}</SmartText></p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
       {category.transformationTechniques && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
      Style Transformation Techniques (Verb-to-Noun Reduction):
    </h5>
    <div style={customStyles.detailGrid}>
      {category.transformationTechniques.map((item, idx) => (
        <div key={idx} style={{...customStyles.detailCard, borderTop: '4px solid #14b8a6', display: 'flex', flexDirection: 'column'}}>

          <strong style={{ display: 'block', color: '#0f766e', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
            <SmartText as="span">{item.type}</SmartText> {/* ADDED SmartText */}
          </strong>

          <p style={{ color: '#4b5563', margin: '0 0 1rem 0', fontSize: '0.9rem'}}>
            <strong style={{ color: '#10b981' }}>Technique:</strong> <SmartText as="span">{item.technique}</SmartText> {/* ADDED SmartText */}
          </p>

          {/* Transformation Example Box */}
          <div style={{...customStyles.patternBox, background: '#f0fdf9', border: '1px solid #ccfbf1', flexGrow: 1}}>
            <div style={{ marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '600' }}>INITIAL (Clause Form):</span>
              <p style={{ fontFamily: 'monospace', margin: '0.25rem 0', fontSize: '0.9rem' }}><SmartText as="span">{item.clauseForm}</SmartText></p> {/* ADDED SmartText */}
            </div>
            <div>
              <span style={{ fontSize: '0.8rem', color: '#0891b2', fontWeight: '600' }}>TARGET (Prepositional/Nominal Form):</span>
              <p style={{ fontFamily: 'monospace', margin: '0.25rem 0', fontSize: '0.9rem' }}><SmartText as="span">{item.prepositionalForm}</SmartText></p> {/* ADDED SmartText */}
            </div>
          </div>

          <div style={{...customStyles.analysisCard, marginTop: '1rem', background: '#f0f9ff', borderLeftColor: '#0ea5e9', fontSize: '0.85rem'}}>
            <strong style={{ color: '#0369a1' }}>Stylistic Effect:</strong> <SmartText as="span">{item.effect}</SmartText> {/* ADDED SmartText */}
          </div>
        </div>
      ))}
    </div>
  </div>
)}
        {/* ✅ NEW: RENDER LOGIC FOR 'commonCombinations' */}
   {category.commonCombinations && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>Common Combinations:</h5>
    {category.commonCombinations.map((combo, idx) => (
      <div key={idx} style={customStyles.modalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <code style={{ ...customStyles.codeHighlight, background: '#dbeafe', color: '#1e40af' }}><SmartText as="span">{combo.verb}</SmartText></code>
          <span style={{ fontStyle: 'italic', color: '#4b5563' }}><SmartText as="span">{combo.meaning}</SmartText></span>
        </div>

        {/* Only show the example box if combo.example has content */}
        {combo.example && (
            <div style={{ ...customStyles.exampleCard, padding: '0.75rem', margin: '0.75rem 0 0 0', background: '#f9fafb' }}>
              <Target size={16} style={{ ...customStyles.exampleIcon, left: '0.75rem', top: '0.75rem' }} />
              <div style={{ fontFamily: 'monospace', marginLeft: '1rem' }}><SmartText as="span">{combo.example}</SmartText></div>
            </div>
        )}

        {/* NEW: Display the note content, processed by SmartText */}
        {combo.note && (
            <p style={{fontSize: '0.9rem', color: '#374151', margin: '0.75rem 0 0 0', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb'}}>
                <strong>{t('grammar.content.note')}:</strong> <SmartText as="span">{combo.note}</SmartText>
            </p>
        )}
      </div>
    ))}
  </div>
)}
        {/* Article/Pronoun Tables */}
        {(category.articleTable || category.pronounTable) && (
          <div style={customStyles.tableContainer}>
            <div style={customStyles.tableHeader}>
               {category.articleTable ? t('grammar.content.articleTable') : t('grammar.content.pronounTable')}
            </div>
            {Object.entries(category.articleTable || category.pronounTable).map(
              ([key, value], index) => (
                <div
                  key={key}
                  style={{
                    ...customStyles.tableRow,
                    background: index % 2 === 0 ? 'white' : '#f9fafb'
                  }}
                >
                  <span style={customStyles.tableKey}>{key.replace(/_/g, ' ')}</span>
                  <div style={customStyles.tableValue}>
                    {typeof value === 'string' ? (
                      <code style={customStyles.codeHighlight}>{value}</code>
                    ) : (
                      Object.entries(value).map(([subKey, subValue]) => (
                        <div key={subKey} style={{ marginBottom: '0.25rem' }}>
                          <span
                            style={{
                              textTransform: 'capitalize',
                              marginRight: '0.5rem',
                              fontSize: '0.9em'
                            }}
                          >
                            {subKey}:
                          </span>
                          <code style={customStyles.codeHighlight}>{subValue}</code>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        )}


      {/* Structure */}
{category.structure && Array.isArray(category.structure) && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
      Grammatical Structures:
    </h5>
    {category.structure.map((item, idx) => (
      <div key={idx} style={{...customStyles.subCategoryCard, marginBottom: '1rem'}}>
        <SmartText as="strong" style={{ display: 'block', color: '#111827', marginBottom: '0.25rem' }}>
          {item.name}
        </SmartText>
        <SmartText as="p" style={{ fontStyle: 'italic', color: '#4b5563', margin: '0 0 0.75rem 0' }}>
          {item.function}
        </SmartText>
        <SmartText as="code" style={{...customStyles.codeHighlight, display: 'block', padding: '0.5rem'}}>
          {item.formula}
        </SmartText>
      </div>
    ))}
  </div>
)}

       {/* Fallback for old string format */}
{category.structure && typeof category.structure === 'string' && (
   <div style={customStyles.ruleCard}>
     <SmartText>
       <strong style={{ color: '#1e40af' }}>Structure:</strong>
       <code style={customStyles.codeHighlight}>{category.structure}</code>
     </SmartText>
   </div>
)}
        {/* Formation Strategy */}
        {category.formationStrategy && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Konjunktiv II Formation Strategy:
            </h5>
            <div style={customStyles.detailGrid}>
              {category.formationStrategy.map((item, idx) => (
                <div key={idx} style={{...customStyles.detailCard, borderTop: `4px solid ${idx === 0 ? '#ef4444' : '#3b82f6'}`}}>
                  <h5 style={{ color: '#111827', fontWeight: '600', marginBottom: '0.5rem' }}>
                    {item.category}
                  </h5>
                  <p style={{fontSize: '0.9rem', color: '#4b5563'}}><strong>Verbs:</strong> <SmartText as="span">{item.verbs}</SmartText></p>
                  <p style={{...customStyles.ruleCard, background: '#f9fafb', padding: '0.75rem'}}><strong>{t('grammar.content.rule')}:</strong><SmartText as="span">{item.rule}</SmartText></p>

                  {/* Renderer for the 'Non-negotiable' type */}
                  {item.forms && (
                    <div style={{marginTop: '1rem'}}>
                        <strong style={{display: 'block', marginBottom: '0.5rem'}}>Forms:</strong>
                        <div>
                            {item.forms.split(', ').map(form => (
                                <code key={form} style={{...customStyles.codeHighlight, marginRight: '0.5rem', marginBottom: '0.5rem', display: 'inline-block'}}>{form}</code>
                            ))}
                        </div>
                        <div style={{marginTop: '0.5rem'}}>
                            {item.examples.map(ex => (
                                <div key={ex} style={{...customStyles.exampleCard, padding: '0.5rem', background: ex.includes('✗') ? '#fee2e2' : '#f0fdf4'}}>
                                    <div style={{fontFamily: 'monospace'}}><SmartText as="span">{ex}</SmartText></div>
                                </div>
                            ))}
                        </div>
                    </div>
                  )}

                  {/* Renderer for the 'Stylistic Choice' type */}
                  {item.comparison && (
                     <div style={{marginTop: '1rem'}}>
                        {item.comparison.map((comp, cIdx) => (
                            <div key={cIdx} style={{...customStyles.patternBox, background: '#f8fafc'}}>
                                <p style={{margin: '0 0 0.5rem 0'}}><strong>Standard Form:</strong><br/><em style={{fontFamily: 'monospace'}}><SmartText as="span">{comp.würde_form}</SmartText></em></p>
                                <p style={{margin: '0'}}><strong>Formal/Literary Form:</strong><br/><em style={{fontFamily: 'monospace'}}><SmartText as="span">{comp.true_form}</SmartText></em></p>
                                <p style={{...customStyles.analysisCard, marginTop: '0.75rem', fontSize: '0.85rem'}}><strong>Register:</strong> <SmartText as="span">{comp.register}</SmartText></p>
                            </div>
                        ))}
                     </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Diplomatic Applications */}
        {category.diplomaticApplications && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Diplomatic Applications of Konjunktiv II:
            </h5>
            <div style={customStyles.detailGrid}>
              {category.diplomaticApplications.map((item, idx) => (
                <div key={idx} style={{...customStyles.detailCard, borderTop: `4px solid #8b5cf6`}}>
                  <h5 style={{ color: '#5b21b6', fontWeight: '600', marginBottom: '0.5rem' }}>
                    <SmartText as="span">{item.function}</SmartText>
                  </h5>
                  <p style={{ fontSize: '0.9rem', color: '#4b5563', marginBottom: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
                    <strong>{t('grammar.content.effect')}:</strong> <SmartText as="span">{item.effect}</SmartText>
                  </p>

                  {/* This part maps over the nested examples array */}
                  <div>
                    {item.examples.map((ex, exIdx) => (
                      <div key={exIdx} style={{...customStyles.exampleCard, padding: '0.75rem', background: '#f5f3ff', border: '1px solid #ddd6fe', marginBottom: '0.5rem'}}>
                        <Target size={16} style={{ ...customStyles.exampleIcon, left: '0.75rem', top: '0.75rem', color: '#7c3aed' }} />
                        <div style={{ fontFamily: 'monospace', marginLeft: '1rem' }}><SmartText as="span">{ex}</SmartText></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Idiomatic Expressions */}
        {category.idiomaticExpressions && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Idiomatic Expressions:
            </h5>
            {category.idiomaticExpressions.map((item, idx) => (
              <div key={idx} style={{...customStyles.modalCard, marginBottom: '1rem', borderLeft: '4px solid #f59e0b'}}>
                <strong style={{ display: 'block', color: '#111827', fontSize: '1.2rem', fontFamily: 'monospace' }}>
                  "<SmartText>{item.phrase}</SmartText>"
                </strong>

                <div style={{margin: '0.75rem 0'}}>
                    <p style={{margin: '0 0 0.5rem 0'}}><span style={{fontSize: '0.8rem', color: '#6b7280'}}>LITERAL:</span> <em>{item.literal}</em></p>
                    <p style={{margin: 0}}><span style={{fontSize: '0.8rem', color: '#6b7280'}}>MEANING:</span> <strong>{item.meaning}</strong></p>
                </div>

                <div style={{...customStyles.analysisCard, background: '#fefce8', borderLeftColor: '#facc15', fontSize: '0.9rem', paddingTop: '0.75rem', borderTop: '1px solid #fde68a'}}>
                  <p style={{margin: '0 0 0.5rem 0'}}><strong>Usage:</strong> {item.usage}</p>
                  <p style={{margin: 0}}><strong>Grammar:</strong><SmartText>{item.grammar}</SmartText></p>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Four Essential Tools */}
        {category.fourEssentialTools && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              The Four Essential Tools of Nominalstil:
            </h5>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              {category.fourEssentialTools.map((item, idx) => {
                // This logic splits the string into its parts
                const parts = item.split(': ');
                const titlePart = parts[0] || '';
                const description = parts[1] || '';
                const number = titlePart.split('.')[0];
                const title = titlePart.split('. ')[1];

                return (
                  <div key={idx} style={{...customStyles.detailCard, position: 'relative', paddingLeft: '3rem'}}>
                    <div style={{
                        position: 'absolute',
                        left: '1rem',
                        top: '1rem',
                        background: '#eef2ff',
                        color: '#4338ca',
                        fontWeight: '600',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1rem'
                    }}>
                      {number}
                    </div>
                    <strong style={{ display: 'block', color: '#111827', marginBottom: '0.25rem' }}>
                      {title}
                    </strong>
                    <p style={{ color: '#4b5563', margin: 0, fontSize: '0.9rem' }}>
                      {description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Nominalization Methods */}
        {category.nominalizationMethods && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Nominalization Methods:
            </h5>
            <div style={customStyles.detailGrid}>
              {category.nominalizationMethods.map((item, idx) => (
                <div key={idx} style={{...customStyles.detailCard, display: 'flex', flexDirection: 'column'}}>
                  <strong style={{ display: 'block', color: '#111827', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                    {item.method}
                  </strong>
                  <p style={{ color: '#4b5563', margin: '0 0 1rem 0' }}>
                    {item.rule}
                  </p>

                  <div style={{ marginBottom: '1rem', flexGrow: 1 }}>
                    {item.examples.map((ex, exIdx) => (
                      <div key={exIdx} style={{...customStyles.exampleCard, fontFamily: 'monospace', padding: '0.5rem', background: '#f9fafb', border: '1px solid #e5e7eb', marginBottom: '0.25rem', paddingLeft: '1rem'}}>
                        {ex}
                      </div>
                    ))}
                  </div>

                  <div style={{...customStyles.analysisCard, background: '#f0f9ff', borderLeftColor: '#0ea5e9', fontSize: '0.9rem'}}>
                    <strong>Usage:</strong> {item.usage}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {category.absoluteSuperlative && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
              The Absolute Superlative (Non-Comparative Intensity)
            </h5>
            {category.absoluteSuperlative.map((item, idx) => (
              <div key={idx} style={{...customStyles.subCategoryCard, borderLeft: '4px solid #f97316'}}>

                <p style={{ color: '#4b5563', margin: '0 0 1rem 0', fontSize: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem'}}>
                  <strong style={{ color: '#9a3412' }}>Function:</strong> <SmartText>{item.function}</SmartText>
                  <br/>
                  <strong style={{ color: '#9a3412' }}>Formation:</strong> <SmartText>{item.formation}</SmartText>
                </p>

                {/* --- Adverbial Forms (e.g., äußerst) --- */}
                <h6 style={{ color: '#374151', fontWeight: '600', marginBottom: '0.75rem' }}>1. Adverbial Intensifiers:</h6>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {(item.adverbialForms || []).map((adv, advIdx) => (
                    <div key={advIdx} style={{...customStyles.patternBox, background: '#fff7ed', border: '1px solid #fed7aa', padding: '0.75rem'}}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <code style={{ ...customStyles.codeHighlight, background: '#f97316', color: 'white', fontSize: '1.1rem', padding: '0.3rem 0.6rem' }}>
                          <SmartText>{adv.adverb}</SmartText>
                        </code>
                        <span style={{ fontStyle: 'italic', color: '#4b5563', textAlign: 'right' }}>
                          (<SmartText>{adv.meaning}</SmartText>)
                        </span>
                      </div>
                      <p style={{ fontFamily: 'monospace', margin: '0 0 0 0', fontSize: '0.9rem' }}>
                        → <SmartText>{adv.example}</SmartText>
                      </p>
                    </div>
                  ))}
                </div>

                {/* --- Prefix Forms (e.g., brand-) --- */}
                <h6 style={{ color: '#374151', fontWeight: '600', marginTop: '1.5rem', marginBottom: '0.75rem' }}>2. Intensifying Prefixes:</h6>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  {(item.prefixForms || []).map((prefix, pIdx) => (
                    <div key={pIdx} style={{...customStyles.detailCard, flex: '1 1 calc(50% - 0.75rem)'}}>
                      <code style={{ ...customStyles.codeHighlight, background: '#fee2e2', color: '#b91c1c', fontSize: '1rem', padding: '0.3rem 0.6rem' }}>
                        <SmartText>{prefix.prefix}</SmartText>
                      </code>
                      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#374151'}}>
                        <SmartText>{prefix.meaning}</SmartText>
                        <br/>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#6b7280' }}>(<SmartText>{prefix.example}</SmartText>)</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
         {/* ✅ NEW: RENDER LOGIC FOR 'complementTypes' (Adjective Complements) */}
         {category.complementTypes && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
              Adjective Complements: Case and Prepositional Requirements
            </h5>
            <div style={customStyles.detailGrid}>
              {category.complementTypes.map((category, idx) => (
                <div key={idx} style={{...customStyles.subCategoryCard, borderTop: `4px solid ${idx === 2 ? '#b91c1c' : '#4f46e5'}`}}>

                  <strong style={{ display: 'block', color: `${idx === 2 ? '#b91c1c' : '#3730a3'}`, fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                    <SmartText>{category.type}</SmartText>
                  </strong>

                  <p style={{ color: '#4b5563', margin: '0 0 1rem 0', fontSize: '0.9rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
                    <SmartText>{category.description}</SmartText>
                    {category.registerNote && <span style={{display: 'block', fontStyle: 'italic', color: '#9c2f2f', marginTop: '0.5rem'}}> (<SmartText>{category.registerNote}</SmartText>)</span>}
                  </p>

                  <h6 style={{ color: '#374151', fontWeight: '600', marginBottom: '0.75rem' }}>Examples:</h6>
                  {category.examples.map((item, exIdx) => (
                    <div key={exIdx} style={{...customStyles.patternBox, background: '#f8fafc', padding: '0.75rem', marginBottom: '0.5rem'}}>
                      <strong style={{ color: '#3730a3' }}><SmartText>{item.adjective}</SmartText></strong>
                      <p style={{ fontFamily: 'monospace', margin: '0.25rem 0', fontSize: '0.9rem' }}><SmartText>{item.example}</SmartText></p>
                      <em style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280' }}>(<SmartText>{item.meaning}</SmartText>)</em>
                      {item.register && <span style={{ display: 'block', fontSize: '0.75rem', color: '#9a3412', marginTop: '0.25rem' }}>**Register:** <SmartText>{item.register}</SmartText></span>}
                    </div>
                  ))}

                </div>
              ))}
            </div>
          </div>
        )}
        {/* Von Dativ Alternative */}
        {category.vonDativAlternative && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              'Von + Dativ' as a Genitive Alternative:
            </h5>
            <div style={customStyles.detailGrid}>
              {category.vonDativAlternative.map((item, idx) => (
                <div key={idx} style={customStyles.detailCard}>
                  <h5 style={{ color: '#4b5569', fontWeight: '600', marginBottom: '0.75rem' }}>
                    <SmartText as="span">{item.context}</SmartText> {/* ADDED SmartText */}
                  </h5>

                  {/* Renderer for the 'Problem/Solution' type */}
                  {item.problem && (
                    <div>
                      <p style={{fontSize: '0.9rem', color: '#374151'}}><strong>Problem:</strong> <SmartText as="span">{item.problem}</SmartText></p> {/* ADDED SmartText */}
                      <div style={{...customStyles.exampleCard, background: '#fee2e2', border: '1px solid #fecaca', fontFamily: 'monospace', padding: '0.5rem'}}>
                        <SmartText as="span">{item.example}</SmartText> {/* ADDED SmartText */}
                      </div>
                      <div style={{...customStyles.exampleCard, background: '#f0fdf4', border: '1px solid #bbf7d0', fontFamily: 'monospace', padding: '0.5rem', marginTop: '0.5rem'}}>
                        <strong>Solution:</strong> <SmartText as="span">{item.solution}</SmartText> {/* ADDED SmartText */}
                      </div>
                    </div>
                  )}

                  {/* Renderer for the 'Note/Comparison' type */}
                  {item.note && (
                    <div>
                        <p style={{fontSize: '0.9rem', color: '#374151'}}><strong>{t('grammar.content.note')}:</strong> <SmartText as="span">{item.note}</SmartText></p> {/* ADDED SmartText */}
                        <div style={{...customStyles.exampleCard, background: '#f0f9ff', border: '1px solid #bae6fd', fontFamily: 'monospace', padding: '0.5rem'}}>
                            <SmartText as="span">{item.example}</SmartText> {/* ADDED SmartText */}
                        </div>
                        <div style={{...customStyles.exampleCard, background: '#f8fafc', border: '1px solid #e2e8f0', fontFamily: 'monospace', padding: '0.5rem', marginTop: '0.5rem'}}>
                            <strong>Compare:</strong> <SmartText as="span">{item.comparison}</SmartText> {/* ADDED SmartText */}
                        </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Participle Types */}
        {category.participleTypes && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
      Participle Construction Types:
    </h5>
    <div style={{display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '1rem'}}>
      {category.participleTypes.map((item, idx) => (
        <div key={idx} style={{...customStyles.detailCard, display: 'flex', flexDirection: 'column'}}>
          <strong style={{ display: 'block', color: '#111827', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
            <SmartText as="span">{item.type}</SmartText> {/* ADDED SmartText */}
          </strong>
          <p style={{ color: '#4b5563', margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
            <strong>Formation:</strong> <code style={customStyles.codeHighlight}><SmartText as="span">{item.formation}</SmartText></code> {/* ADDED SmartText */}
          </p>
          <p style={{ color: '#4b5563', margin: '0 0 1rem 0', fontSize: '0.9rem', flexGrow: 1 }}>
            <strong>Function:</strong> <SmartText as="span">{item.function}</SmartText> {/* ADDED SmartText */}
          </p>

          {/* Transformation Example Box */}
          <div style={{...customStyles.patternBox, background: '#f8fafc'}}>
            <div style={{ marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '600' }}>RELATIVE CLAUSE:</span>
              <p style={{ fontFamily: 'monospace', margin: '0.25rem 0', fontSize: '0.9rem' }}><SmartText as="span">{item.example.relative}</SmartText></p> {/* ADDED SmartText */}
            </div>
            <div>
              <span style={{ fontSize: '0.8rem', color: '#16a34a', fontWeight: '600' }}>PARTICIPLE CONSTRUCTION:</span>
              <p style={{ fontFamily: 'monospace', margin: '0.25rem 0', fontSize: '0.9rem' }}><SmartText as="span">{item.example.participle}</SmartText></p> {/* ADDED SmartText */}
            </div>
          </div>
          <div style={{...customStyles.analysisCard, marginTop: '1rem', fontSize: '0.85rem'}}>
            <strong>Compression Effect:</strong> <SmartText as="span">{item.example.compression}</SmartText> {/* ADDED SmartText */}
          </div>
        </div>
      ))}
    </div>
  </div>
)}
        {/* N-Declension Noun Categories */}
        {category.nounCategories && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
      N-Declension Noun Categories:
    </h5>
    <div style={customStyles.detailGrid}>
      {category.nounCategories.map((item, idx) => (
        <div key={idx} style={{...customStyles.detailCard, display: 'flex', flexDirection: 'column'}}>
          <h5 style={{ color: '#111827', fontWeight: '600', marginBottom: '0.5rem' }}>
            <SmartText as="span">{item.category}</SmartText> {/* ADDED SmartText */}
          </h5>

          {item.pattern && (
            <p style={{...customStyles.ruleCard, background: '#f9fafb', padding: '0.5rem', fontSize: '0.9rem'}}>
              <strong>{t('grammar.content.pattern')}:</strong> <SmartText as="span">{item.pattern}</SmartText> {/* ADDED SmartText */}
            </p>
          )}

          {item.suffixes && (
            <div style={{marginBottom: '1rem'}}>
              {item.suffixes.map(s => <code key={s} style={{...customStyles.codeHighlight, marginRight: '0.5rem'}}><SmartText as="span">{s}</SmartText></code>)} {/* ADDED SmartText */}
            </div>
          )}

          {item.examples && (
            <div style={{marginBottom: '1rem', flexGrow: 1}}>
              {item.examples.map(ex => (
                <div key={ex} style={{...customStyles.exampleCard, fontFamily: 'monospace', padding: '0.5rem', background: '#f8fafc', border: '1px solid #e5e7eb', marginBottom: '0.25rem', paddingLeft: '1rem'}}>
                  <SmartText as="span">{ex}</SmartText> {/* ADDED SmartText */}
                </div>
              ))}
            </div>
          )}

          {item.declension && (
             <div style={{...customStyles.exampleCard, fontFamily: 'monospace', padding: '0.75rem', background: '#f8fafc', border: '1px solid #e5e7eb', marginBottom: '1rem', fontSize: '0.9rem', flexGrow: 1}}>
                <SmartText as="span">{item.declension}</SmartText> {/* ADDED SmartText */}
              </div>
          )}

          {item.note && (
            <div style={{...customStyles.noteCard, marginTop: 'auto'}}>
              <Info size={20} style={{ flexShrink: 0 }} />
              <div><SmartText>{item.note}</SmartText></div> {/* ADDED SmartText */}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
)}
        {/* Master Constructions & Contextual Guidance */}
     {category.masterConstructions && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Master Constructions:
            </h5>
            {category.masterConstructions.map((item, idx) => (
              <div key={idx} style={customStyles.masterExampleCard}>
                <strong style={{ display: 'block', color: '#111827', marginBottom: '0.75rem' }}>
                  <SmartText>{item.title}</SmartText>
                </strong>
                <div style={{...customStyles.exampleCard, fontFamily: 'monospace', marginBottom: '1rem'}}>

                    <SmartText as="span">{item.sentence || item.example}</SmartText>
                  </div>
                <div style={{...customStyles.analysisCard, padding: '1rem'}}>
                 <strong>{t('grammar.content.analysis')}:</strong>
                  <ul style={{paddingLeft: '1.25rem', margin: '0.5rem 0 0 0'}}>
                    {item.analysis.map((point, pIdx) => (
                      <li key={pIdx} style={{marginBottom: '0.25rem'}}><SmartText>{point}</SmartText></li>
                    ))}
                  </ul>
                </div>
                 <p style={{...customStyles.tipCard, background: '#f0f9ff', color: '#075985', marginTop: '1rem', padding: '0.75rem'}}>
                   <strong>{t('grammar.content.effect')}:</strong><SmartText>{item.effect}</SmartText>
                 </p>
              </div>
            ))}
          </div>
        )}

        {/* Contextual Guidance */}
        {category.contextualGuidance && (
          <div style={{ marginTop: '2rem' }}>
            <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Contextual Guidance:
            </h5>
            <div style={{display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '1rem'}}>
              {category.contextualGuidance.map((item, idx) => (
                <div key={idx} style={{...customStyles.detailCard, borderTop: `4px solid #0ea5e9`}}>
                  <h5 style={{color: '#0369a1', fontWeight: '600', marginBottom: '0.5rem'}}>{item.context}</h5>
                  <p style={{fontSize: '0.9rem', color: '#374151'}}><strong>Usage:</strong> <SmartText>{item.usage}</SmartText></p>
                  <div style={{...customStyles.exampleCard, fontFamily: 'monospace', padding: '0.5rem', background: '#f8fafc'}}>
                      <SmartText>{item.example || item.better}</SmartText>
                  </div>
                  <p style={{fontSize: '0.85rem', color: '#4b5563', margin: '0.5rem 0 0 0', fontStyle: 'italic'}}>Effect: <SmartText>{item.effect}</SmartText></p>
                </div>
              ))}
            </div>
          </div>
        )}
        {category.thematicGroups && (
  <div style={{ marginTop: '1.5rem' }}>
    <h5 style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>Thematic Verb Groups:</h5>
    {Object.entries(category.thematicGroups).map(([key, verbs]) => (
      <div key={key} style={customStyles.subCategoryCard}>
        <h4 style={{ color: '#111827', fontWeight: '600', marginBottom: '0.5rem' }}>
          <SmartText as="span">{formatSubCategoryTitle(key)}</SmartText>
        </h4>
        {verbs.map(verb => (
           <div key={verb} style={{...customStyles.exampleCard, padding: '0.5rem 1rem', marginBottom: '0.5rem'}}>
             <div style={{ fontFamily: 'monospace' }}><SmartText>{verb}</SmartText></div>
           </div>
        ))}
      </div>
    ))}
  </div>
)}
        {/* Examples */}
{category.examples && Array.isArray(category.examples) && (
  <div style={{ marginTop: '1rem' }}>
    <h5 style={{ marginBottom: '0.75rem', color: '#374151', fontWeight: '600' }}>
      Examples:
    </h5>
    {category.examples.map((ex, idx) => (
      <div key={idx} style={{...customStyles.exampleCard, padding: '1rem', background: '#f8fafc', border: '1px solid #e2e8f0' }}>

        {/* Condition for the new Cardinal Numbers example structure */}
        {ex.number && ex.german ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <strong style={{...customStyles.codeHighlight, background: '#1e3a8a', fontSize: '1.25rem', padding: '0.5rem 1rem'}}>{ex.number}</strong>
              <SmartText as="span" style={{fontFamily: 'monospace', fontSize: '1.1rem', color: '#1e40af', fontWeight: 500}}>{ex.german}</SmartText>
            </div>
            <div style={{...customStyles.analysisCard, marginTop: '0.5rem', padding: '0.5rem 0.75rem'}}>
              <strong>Breakdown:</strong> <SmartText>{ex.breakdown}</SmartText>
              <br/>
              <em>(<SmartText>{ex.literal}</SmartText>)</em>
            </div>
          </div>

        /* Condition for 'location' and 'direction' keys */
        ) : ex.location && ex.direction ? (
          <div>
            <div style={{ marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '600' }}>LOCATION (Wo? → Dativ):</span>
              <SmartText as="p" style={{ fontFamily: 'monospace', margin: '0.25rem 0', color: '#0f172a' }}>{ex.location}</SmartText>
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#4f46e5', fontWeight: '600' }}>DIRECTION (Wohin? → Akkusativ):</span>
              <SmartText as="p" style={{ fontFamily: 'monospace', margin: '0.25rem 0', color: '#0f172a' }}>{ex.direction}</SmartText>
            </div>
            {ex.explanation && (
               <SmartText as="div" style={{ fontSize: '0.875rem', color: '#4b5563', marginTop: '0.5rem', paddingTop: '0.5rem', fontStyle: 'italic', borderTop: '1px solid #e2e8f0' }}>
                 {ex.explanation}
               </SmartText>
            )}
          </div>

        /* Condition for 'separate' and 'combined' keys */
        ) : ex.separate && ex.combined ? (
          <div>
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '600' }}>BEFORE:</span>
              <SmartText as="p" style={{ fontFamily: 'monospace', margin: '0.25rem 0' }}>{ex.separate}</SmartText>
            </div>
            <div>
              <span style={{ fontSize: '0.8rem', color: '#16a34a', fontWeight: '600' }}>AFTER:</span>
              <SmartText as="p" style={{ fontFamily: 'monospace', margin: '0.25rem 0' }}>{ex.combined}</SmartText>
            </div>
            {ex.translation && (
               <SmartText as="div" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem', fontStyle: 'italic' }}>
                 → {ex.translation}
               </SmartText>
            )}
          </div>

        /* Condition for 'alternative' key */
        ) : ex.alternative ? (
          <div>
            <div style={{ marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: '600' }}>SHORTCUT (`wo`):</span>
              <SmartText as="p" style={{ fontFamily: 'monospace', margin: '0.25rem 0' }}>{ex.sentence}</SmartText>
            </div>
            <div>
              <span style={{ fontSize: '0.8rem', color: '#4f46e5', fontWeight: '600' }}>FORMAL ALTERNATIVE:</span>
              <SmartText as="p" style={{ fontFamily: 'monospace', margin: '0.25rem 0' }}>{ex.alternative}</SmartText>
            </div>
            {ex.translation && (
               <SmartText as="div" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem', fontStyle: 'italic' }}>
                 → {ex.translation}
               </SmartText>
            )}
          </div>
        ) : ex.construction ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <SmartText as="code" style={{ ...customStyles.codeHighlight, background: '#dbeafe', color: '#1e40af', fontSize: '1.1rem' }}>
                {ex.construction}
              </SmartText>
              <SmartText as="span" style={{ fontStyle: 'italic', color: '#4b5563', textAlign: 'right' }}>
                {ex.meaning}
              </SmartText>
            </div>
            <SmartText as="p" style={{ fontFamily: 'monospace', margin: '0 0 0.5rem 0' }}>{ex.example}</SmartText>
            <p style={{fontSize: '0.9rem', color: '#374151', margin: 0}}>
              <strong>{t('grammar.content.context')}:</strong> <SmartText>{ex.context}</SmartText>
            </p>
          </div>
        /* Fallback for all other normal examples */
        ) : (
          <div>
            {/* This part handles simple examples with sentence, translation, analysis, etc. */}
            <Target style={customStyles.exampleIcon} size={18} />
            <div style={{ marginLeft: '2rem' }}>
                {ex.context && (
                    <div style={{
                        background: '#eef2ff',
                        color: '#4338ca',
                        padding: '0.25rem 0.6rem',
                        borderRadius: '1rem',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        display: 'inline-block',
                        marginBottom: '0.5rem'
                    }}>
                       <SmartText>{ex.context}</SmartText>
                    </div>
                )}
                <SmartText as="div" style={{ fontFamily: 'monospace' }}>
                  {typeof ex === 'string' ? ex : (ex.sentence || ex.example || ex.text)}
                </SmartText>
                {(ex.translation || ex.meaning || ex.nuance) && (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem', fontStyle: 'italic' }}>
                    → <SmartText>{ex.translation || ex.meaning || ex.nuance}</SmartText>
                  </div>
                )}
                {ex.breakdown && (
                  <div style={{...customStyles.analysisCard, marginTop: '0.5rem', padding: '0.5rem 0.75rem'}}>
                    <strong>Breakdown:</strong> <SmartText>{ex.breakdown}</SmartText>
                  </div>
                )}
                {ex.analysis && (
                  <div style={{...customStyles.analysisCard, marginTop: '0.5rem', padding: '0.5rem 0.75rem'}}>
                    <strong>{t('grammar.content.analysis')}:</strong><SmartText>{ex.analysis}</SmartText>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>
    ))}
  </div>
)}

        {/* Master Examples */}
        {category.masterExamples && (
          <div style={{ marginTop: '1.5rem' }}>
            <h5 style={{ marginBottom: '0.75rem', color: '#374151', fontWeight: '600' }}>
              Master Constructions:
            </h5>
            {category.masterExamples.map((ex, index) => (
              <div key={index} style={customStyles.masterExampleCard}>
                <strong style={{ display: 'block', color: '#111827', marginBottom: '0.5rem' }}>
                  <SmartText>{ex.title}</SmartText>
                </strong>
                <div style={{ ...customStyles.exampleCard, margin: '0.75rem 0' }}>
                  <div>"<SmartText>{ex.sentence}</SmartText>"</div>
                </div>
                <div style={customStyles.analysisCard}>
                  <strong>{t('grammar.content.analysis')}:</strong> <SmartText>{ex.analysis}</SmartText>
                </div>
{ex.technique && (
    <div style={{...customStyles.tipCard, marginTop: '0.75rem', background: '#eef2ff', padding: '0.75rem'}}>
        <Lightbulb size={20} color="#4f46e5" />
        <div>
            <strong style={{ color: '#4338ca' }}>
                Technique:
            </strong>
            <div style={{ marginTop: '0.25rem' }}>
                {ex.technique}
            </div>
        </div>
    </div>
)}
              </div>
            ))}
          </div>
        )}

        {/* Generic Lists (techniques, principles, etc.) */}
        {(category.techniques ||
          category.principles ||
          category.situations ||
          category.regions ||
          category.list ||
          category.items) && (
          <div style={customStyles.detailGrid}>
            {(category.techniques ||
              category.principles ||
              category.situations ||
              category.regions ||
              category.list ||
              category.items ||
              []).map((item, idx) => (
              <div key={idx} style={customStyles.detailCard}>
                {(item.title || item.name || item.category || item.type) && (
                  <h5 style={{ color: '#4b5569', fontWeight: '600', marginBottom: '0.5rem' }}>
                    {item.title || item.name || item.category || item.type}
                  </h5>
                )}
                {(item.description || item.effect || item.analysis || item.meaning) && (
                  <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem' }}>
                    {item.description || item.effect || item.analysis || item.meaning}
                  </p>
                )}
                {item.structure && (
                  <code
                    style={{
                      ...customStyles.codeHighlight,
                      display: 'inline-block',
                      marginTop: '0.5rem'
                    }}
                  >
                    {item.structure}
                  </code>
                )}
                {item.example && (
                  <div
                    style={{
                      background: '#f9fafb',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      marginTop: '0.5rem',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem'
                    }}
                  >
                    {typeof item.example === 'string' ? item.example : JSON.stringify(item.example)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Notes */}
        {category.notes && (
          <div style={customStyles.noteCard}>
            <Info size={20} style={{ color: '#f59e0b', flexShrink: 0 }} />
            <div>
              <strong>{t('grammar.content.notes')}</strong> <SmartText>{category.notes}</SmartText>
            </div>
          </div>
        )}

        {/* Key Points / Memory Tricks */}
        {(category.keyPoint || category.memory_trick) && (
          <div style={customStyles.tipCard}>
            <Lightbulb size={20} color="#f59e0b" />
            <div>
              <strong style={{ color: '#d97706' }}>
                {category.memory_trick ? t('grammar.content.memoryTrick') : t('grammar.content.keyPoint')}
              </strong>
              <div style={{ marginTop: '0.25rem' }}>
                 <SmartText>{category.keyPoint || category.memory_trick}</SmartText>
              </div>
            </div>
          </div>
        )}
      </div>
    );
};

  // The main dispatch renderer
  const renderSectionContent = (section) => {
    if (!section) return null;

    switch (section.type) {
       case 'summary':
        return (
          <div style={customStyles.highlightBox}>
            <strong>{t('grammar.content.corePrinciple')}</strong> <SmartText as="span">{section.data}</SmartText>
          </div>
        );

      case 'category':
        return renderCategoryContent(section.data);

      case 'forms':
        return (
          <GrammarExplanationRenderer
            explanation={{ forms: section.data.forms, byAgent: section.data.byAgent }}
            styles={customStyles}
            userProgress={userProgress}
            pillar={pillar}
            level={level}
          />
        );

      case 'uses':
        return (
          <GrammarExplanationRenderer
            explanation={{
              uses: section.data.uses,
              forms: { special_forms: section.data.special_forms }
            }}
            styles={customStyles}
            userProgress={userProgress}
            pillar={pillar}
            level={level}
          />
        );

      default:
         return <div>{t('grammar.content.unknownSection')}</div>;
    }
  };

return (
   <div style={{
     animation: 'fadeIn 0.4s ease-out',
     width: '100%',
     maxWidth: '100%',
     padding: isMobile ? '0 0.5rem' : '0',
     boxSizing: 'border-box',
     backgroundColor: '#FFFFFF',
backgroundImage: `
  linear-gradient(0deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent),
  linear-gradient(90deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent)
`,
backgroundSize: '80px 80px',
   }}>
     {renderSectionContent(section)}
   </div>
 );
};

export default InteractiveGrammarContent;
